import {
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { onlineManager, QueryClientProvider } from '@tanstack/react-query';
import type { MutableSnapshot } from 'recoil';
import { RecoilRoot } from 'recoil';

import { I18nWrapper } from '@cognite/cdf-i18n-utils';
import { getToken, getUserInformation } from '@cognite/cdf-sdk-singleton';
import type { AuthStateUser } from '@cognite/e2e-auth';
import type { CogniteClient } from '@cognite/sdk';

import { translations } from '../common/i18n/i18n';
import { APP_ID } from '../features/auth/utils';
import { defaultAppConfig } from '../features/app-config/default-config';
import { infieldProjectUrlAdapter } from '../features/routing/infield-project-url-adapter';
import { TRANSLATION_NAMESPACES } from '../features/i18n/constants';

import { AppProvidersContext } from './app-providers.context';
import { ErrorBoundaryProvider } from './error-boundary-provider/error-boundary-provider';
import { ThemeProviderExtended } from './theme/theme-provider-extended';

type Props = {
  mock?: {
    client?: CogniteClient;
    user?: AuthStateUser;
  };
  recoilInitialState?: (mutableSnapshot: MutableSnapshot) => void;
};

export const AppProviders: FC<PropsWithChildren<Props>> = ({
  children,
  mock,
  recoilInitialState,
}) => {
  return (
    <I18nWrapper
      translations={translations}
      defaultNamespace={TRANSLATION_NAMESPACES.checklist}
    >
      <AppProvidersInner mock={mock} recoilInitialState={recoilInitialState}>
        {children}
      </AppProvidersInner>
    </I18nWrapper>
  );
};

export const AppProvidersInner: FC<PropsWithChildren<Props>> = ({
  children,
  mock,
  recoilInitialState,
}) => {
  const {
    Router,
    AppProviderSwitcher,
    AiProvider,
    AuthProvider,
    OfflineAuthProvider,
    AuthConsumer,
    hasOfflineAuthSession,
    MetricsProvider,
    FlagProvider,
    isProduction,
    getRouterBasename,
    queryClient,
    DeviceProvider,
    useInfieldLanguage,
    registerInfieldLanguageInMetrics,
  } = useContext(AppProvidersContext);

  const basename = getRouterBasename();
  const { i18n } = useTranslation();

  const fdmServiceProviderMock = mock
    ? {
        ...mock,
        appConfig: defaultAppConfig,
      }
    : undefined;

  const { selectedLanguage } = useInfieldLanguage();

  useEffect(() => {
    registerInfieldLanguageInMetrics(selectedLanguage);
  }, [selectedLanguage, registerInfieldLanguageInMetrics]);

  // Mount the offline auth provider on a real offline reload when a snapshot
  // exists. Test locally with a service worker via `nx preview` (plain
  // `nx serve` has no service worker, so an offline reload shows the dinosaur).
  // Use react-query's onlineManager rather than navigator.onLine, which can be
  // inaccurate (see TanStack/query#5679). Decided once on mount so a later
  // re-render after reconnecting doesn't swap providers and remount the app.
  const [shouldUseOfflineAuth] = useState(
    () => !mock && hasOfflineAuthSession() && !onlineManager.isOnline()
  );

  const authenticatedApp = (
    <AuthConsumer>
      {(auth) =>
        auth && (
          <MetricsProvider
            client={auth.client}
            appName="infield"
            serviceName="infield"
            environment={isProduction() ? 'production' : 'staging'}
          >
            <FlagProvider
              appName="InField"
              organizationId={auth?.authState?.projectInfo?.org}
              context={{
                properties: {
                  userId: auth.client.project,
                },
              }}
              getToken={getToken}
              getUserInformation={getUserInformation}
            >
              <DeviceProvider>
                <AppProviderSwitcher
                  i18nLanguage={selectedLanguage.code}
                  authClient={auth.client}
                  queryClient={queryClient}
                  fdmServiceProviderMock={fdmServiceProviderMock}
                  userMock={mock?.user}
                >
                  {children}
                </AppProviderSwitcher>
              </DeviceProvider>
            </FlagProvider>
          </MetricsProvider>
        )
      }
    </AuthConsumer>
  );

  // Pick the provider up front so the tree below mounts it once. The two
  // providers take different props (OfflineAuthProvider only needs appId/appName),
  // so we select the configured element rather than a bare component.
  const authProvider = shouldUseOfflineAuth ? (
    <OfflineAuthProvider appId={APP_ID} appName="Cognite InField">
      {authenticatedApp}
    </OfflineAuthProvider>
  ) : (
    <AuthProvider
      appId={APP_ID}
      appName="Cognite InField"
      mock={mock}
      useProductionAadApp={isProduction()}
      projectUrlAdapter={infieldProjectUrlAdapter}
      enableOfflineToken
    >
      {authenticatedApp}
    </AuthProvider>
  );

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundaryProvider>
        <ThemeProviderExtended>
          <Router
            basename={basename}
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true,
            }}
          >
            <RecoilRoot initializeState={recoilInitialState}>
              <QueryClientProvider client={queryClient}>
                <AiProvider customQueryClient={queryClient}>
                  {authProvider}
                </AiProvider>
              </QueryClientProvider>
            </RecoilRoot>
          </Router>
        </ThemeProviderExtended>
      </ErrorBoundaryProvider>
    </I18nextProvider>
  );
};
